import { useState, useCallback } from 'react';
import { searchMovieOrTVShow, getDetailedResultsBatch } from '../services/api';

export function useSearch() {
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  const fetchSuggestions = useCallback(async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const results = await searchMovieOrTVShow(query);
      const filtered = (results || [])
        .filter(item => ['movie', 'tv'].includes(item.media_type) && item.poster_path)
        .slice(0, 5);
      setSuggestions(filtered);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  }, []);

  const handleSearch = useCallback(async (setResults, setLoading, overrideQuery) => {
    const query = overrideQuery || searchQuery;
    
    if (!query || !query.trim()) {
      setErrorMessage('Please enter a title to search.');
      return;
    }

    setErrorMessage('');
    setLoading(true);

    try {
      const searchResults = await searchMovieOrTVShow(query.trim());
      
      if (!searchResults || searchResults.length === 0) {
        setLoading(false);
        setResults([]);
        setErrorMessage(`No movies or TV shows found for "${query}".`);
        return;
      }

      // Filter duplicates and valid items with posters
      const uniqueResultsMap = new Map();
      searchResults.forEach(item => {
        if (!uniqueResultsMap.has(item.id) && ['movie', 'tv'].includes(item.media_type)) {
          uniqueResultsMap.set(item.id, item);
        }
      });

      const itemsToProcess = Array.from(uniqueResultsMap.values()).slice(0, 8);
      
      if (itemsToProcess.length === 0) {
        setLoading(false);
        setResults([]);
        setErrorMessage(`No movies or TV shows found for "${query}".`);
        return;
      }

      // Immediate preliminary presentation for zero stale time
      const preliminary = itemsToProcess.slice(0, 6).map(item => ({
        ...item,
        mediaType: item.media_type,
        tmdbURL: `https://www.themoviedb.org/${item.media_type}/${item.id}`,
      }));
      setResults(preliminary);
      setLoading(false);

      // Hydrate with trailers, streaming providers, IMDb
      const detailedResults = await getDetailedResultsBatch(itemsToProcess, null, 3);
      if (detailedResults && detailedResults.length > 0) {
        setResults(detailedResults.slice(0, 6));
      }
    } catch (error) {
      console.error('Search error:', error);
      setErrorMessage('Something went wrong while searching. Please try again.');
      setLoading(false);
    }
  }, [searchQuery]);

  return {
    searchQuery,
    setSearchQuery,
    suggestions,
    setSuggestions,
    fetchSuggestions,
    errorMessage,
    setErrorMessage,
    handleSearch
  };
}